import SurveyBaseController from "./surveys/survey_base_controller"
import db from "../field_db"
import { queueImage, requestPersistentStorage, newCaptureId, placeIsPoorSignal, uploadPhotoFromMemory } from "../field_image_queue"
import { readCsrfToken } from "../field_session"
import { compressImageWithRetry, MAX_INPUT_BYTES } from "../field_image_compressor"
import { logEvent, logError, setLogContext } from "../field_logger"
import { showFieldFlash } from "../lib/field_flash"
import { withImageFilename } from "../lib/image_filename"
import { buildSubmissionMeta } from "../field_submission_meta"
import { readMissionSort, sortCachedResponses } from "../lib/place_mission_order"
import { startBlockReason, startableNow } from "../lib/field_start_rule"

// Offline app shell controller.
//
// Renders the Tasker's cached_responses from IndexedDB as a list of cards.
// When a card is tapped, loads the SurveyJS form from the cached survey_json.
// When the survey completes, queues the result to pending_responses for sync-up.
// Every answer change is autosaved (debounced) into cached_responses, the same
// as the online result editor, so a killed tab or a one-page survey never
// loses what the tasker entered (card #3827).
//
// Mounted on the /field/offline-app shell page which is served by the service
// worker when /field/responses or /field/places/* are requested while offline.
//
// Targets:
//   listView       -- the response list container (shown by default)
//   list           -- where response cards are injected
//   empty          -- shown when no cached responses exist
//   surveyView     -- the survey form container (hidden by default)
//   surveyTitle    -- heading for the survey view
//   surveySubtitle -- subheading for the survey view
//   surveyContainer -- SurveyJS mount point

export default class extends SurveyBaseController {
  static targets = [
    "listView", "list", "empty", "emptyText",
    "surveyView", "surveyTitle", "surveySubtitle", "surveyContainer",
    "placesView", "placesList", "placesEmpty",
    "placeShowView", "placeShowContainer", "placeShowTitle"
  ]

  connect() {
    super.connect() // registers SurveyJS custom questions
    this._activeSurvey = null
    this._activeRecord = null
    requestPersistentStorage().catch(() => {})

    // Refresh when a sync completes (new data may be available)
    this._syncHandler = () => {
      if (this._isPlaceShowPath()) {
        this._renderPlaceShow(this._placeIdFromPath())
      } else if (this._isPlacesPath()) {
        this._renderPlaces()
      } else {
        this._render()
      }
    }
    window.addEventListener("field:prefetch-complete", this._syncHandler)

    if (this._isPlaceShowPath()) {
      this._showPlaceShow()
      this._renderPlaceShow(this._placeIdFromPath())
    } else if (this._isPlacesPath()) {
      this._showPlaces()
      this._renderPlaces()
    } else {
      this._render()
    }
  }

  disconnect() {
    window.removeEventListener("field:prefetch-complete", this._syncHandler)
    this._flushAutosave()
  }

  backToList() {
    if (this._inPlaceShow) {
      this.backFromPlaceShow()
      return
    }
    this._flushAutosave()
    if (this._activeSurvey) {
      this._activeSurvey.dispose?.()
      this._activeSurvey = null
      this._activeRecord = null
    }
    if (this.hasSurveyContainerTarget) this.surveyContainerTarget.innerHTML = ""
    this._showList()
    this._render()
  }

  backFromPlaceShow() {
    this._flushAutosave()
    if (this._activeSurvey) {
      this._activeSurvey.dispose?.()
      this._activeSurvey = null
      this._activeRecord = null
    }
    if (this.hasSurveyContainerTarget) this.surveyContainerTarget.innerHTML = ""
    if (this.hasPlaceShowViewTarget) this.placeShowViewTarget.classList.add("hidden")
    this._inPlaceShow = false
    this._showPlaces()
    this._renderPlaces()
  }

  // -- private --

  async _render() {
    const records = await db.cached_responses.toArray()

    if (this.hasListTarget) this.listTarget.innerHTML = ""

    if (records.length === 0) {
      this._showEmpty()
      return
    }

    this._hideEmpty()
    const sorted = records.slice().sort((a, b) => {
      if (a.scheduledAt && b.scheduledAt) return a.scheduledAt < b.scheduledAt ? -1 : 1
      if (a.scheduledAt) return -1
      if (b.scheduledAt) return 1
      return 0
    })

    for (const record of sorted) {
      const card = await this._buildCard(record)
      this.listTarget.appendChild(card)
    }
  }

  async _buildCard(record) {
    const place = record.placeId ? await db.cached_places.get(record.placeId) : null
    const wrapper = document.createElement("div")
    // A completed response (card #3827 writes "ended" after an online
    // complete) is shown but can't be reopened and completed again offline.
    // Card #4152: a todo or not-yet-due scheduled response can't be opened by a
    // tap either -- Start only works for work scheduled today or earlier.
    const startPrompt = this._startPrompt(record)
    if (record.status === "ended" || startPrompt) {
      wrapper.className = "bg-white rounded-2xl border border-slate-200 p-4"
    } else {
      wrapper.className = "bg-white rounded-2xl border border-slate-200 p-4 active:bg-slate-50 cursor-pointer"
      wrapper.addEventListener("click", () => this._openSurvey(record))
    }

    const statusColour = this._statusColour(record.status)
    const scheduledText = record.scheduledAt
      ? new Date(record.scheduledAt).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" })
      : "Unscheduled"

    wrapper.innerHTML = `
      <div class="flex items-start justify-between gap-3">
        <div class="min-w-0">
          <p class="text-sm font-semibold text-slate-800 truncate">${this._escHtml(record.missionName || "Survey")}</p>
          <p class="text-xs text-slate-500 mt-0.5 truncate">${this._escHtml(place?.name || "")}</p>
        </div>
        <span class="shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColour}">
          ${this._escHtml(this._statusLabel(record.status))}
        </span>
      </div>
      <p class="text-xs text-slate-400 mt-2">${scheduledText}</p>
      ${startPrompt ? `<p class="text-xs font-medium text-slate-500 mt-1">${this._escHtml(startPrompt)}</p>` : ""}
    `
    return wrapper
  }

  // Card #4152 -- mirrors Response#start_block_reason (lib/field_start_rule.js).
  // The prompt shown in place of Start for a todo or not-yet-due scheduled
  // response, or null when it can be started or continued. The cache holds no
  // mission status, so whether the mission is live is left to the server.
  _startPrompt(record) {
    const reason = startBlockReason(record)
    if (reason === "unscheduled") return "Schedule this mission to start it"
    if (reason === "future") {
      const day = new Date(record.scheduledAt).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" })
      return `Scheduled for ${day}`
    }
    return null
  }

  async _openSurvey(record) {
    this._activeRecord = record
    this._showSurvey(record)

    // Ambient context for app_logger -- this shell has no per-response Stimulus
    // values (unlike result_editor_controller), so the ids only become known
    // once a specific cached response is opened. No missionId client-side;
    // the server derives it from response_id when logs are synced.
    setLogContext({ responseId: record.responseId, placeId: record.placeId })

    if (this.hasSurveyContainerTarget) this.surveyContainerTarget.innerHTML = ""

    const survey = this.initializeSurvey("edit")
    this._activeSurvey = survey

    if (record.surveyJson) {
      // Card #4451 -- only relax required-question enforcement for an MC
      // re-editing a response that has already ended (Sept 2025 intent,
      // commit ad1206c19). A first-time Tasker completion (todo/started)
      // must keep isRequired as the survey definition set it.
      this._convertPhotoQuestions(record.surveyJson, record.status === "ended")
      survey.fromJSON(record.surveyJson)
    }
    if (record.resultJson) {
      survey.data = record.resultJson
    }

    // Cards #4031/#3073 -- cached last-visit ranging hints (null = flag off).
    this._rangingHints = record.rangingHints ?? null
    this._rangingBarcodes = record.rangingBarcodes ?? null
    // Card #4692 -- cached open allocation/TIO incoming-stock badge.
    this._rangingIncoming = record.rangingIncoming ?? null

    survey.onUploadFiles.add((s, options) => this._handleUpload(s, options))
    survey.onClearFiles.add((s, options) => { options.callback("success") })

    survey.onComplete.add((s, options) => {
      options.showSaveInProgress()
      // Completion owns the record from here -- a pending autosave must not
      // re-create the cached_responses row _queueAndReturn deletes.
      this._cancelAutosave()
      this._queueAndReturn(record, s.data, s).then(() => {
        options.showSaveSuccess("Saved offline!")
      })
    })

    // Autosave on every answer change (card #3827) -- previously answers were
    // only persisted on a page change (never fires on a one-page survey) or
    // on complete, so a photo's answer never reached cached_responses and the
    // photo synced with nothing to attach it to.
    survey.onValueChanged.add((s) => {
      this._scheduleAutosave(record, s.data)
    })

    // Save partial results back to cache on each page change
    survey.onCurrentPageChanged.add(() => {
      this._cancelAutosave()
      this._savePartialToCache(record, survey.data)
    })

    if (this.hasSurveyContainerTarget) {
      survey.render(this.surveyContainerTarget)
    }
  }

  // Card #3843 -- stamps _submission_meta much as the online editor does
  // (result_editor_controller#_ensureSubmissionMeta): expected_image_count,
  // so ResponseImageShortfallCheckJob can see this completion, and captured_at,
  // so the latest-wins guard treats it as a dated snapshot rather than a
  // legacy client. `survey` is this shell's own SurveyJS model, whose photo
  // questions _convertPhotoQuestions has already made type "file" -- exactly
  // what expectedImageCount counts.
  async _queueAndReturn(record, data, survey) {
    const place = record.placeId ? await db.cached_places.get(record.placeId) : null
    // Always a fresh stamp: the shell loaded record.resultJson, which can
    // already hold the _submission_meta of an earlier online "save and back"
    // -- its expected_image_count and captured_at describe that save, not
    // this completion. Stamped once here per completion; a replay only
    // refreshes retry_count/network_level (refreshRetryMeta), never this.
    if (data) {
      const meta = buildSubmissionMeta(survey, data)
      if (meta) data = { ...data, _submission_meta: meta }
    }
    await db.pending_responses.add({
      responseId:     record.responseId,
      surveyableType: "Response",
      surveyData:     data,
      completeUrl:    record.completeUrl,
      missionName:    record.missionName,
      placeName:      place?.name,
      placeId:        record.placeId,
      status:         "pending",
      queuedAt:       new Date().toISOString()
    })
    logEvent("response", "Survey completed offline, queued for sync", {
      responseId:  record.responseId,
      missionName: record.missionName,
      placeName:   place?.name
    }).catch(() => {})

    // Remove from cache -- it's now queued for sync
    const existing = await db.cached_responses
      .where("responseId").equals(record.responseId)
      .first()
    if (existing) await db.cached_responses.delete(existing.id)

    // Navigate back to list after a short delay
    setTimeout(() => this.backToList(), 1500)
  }

  // Debounced like result_editor_controller's _scheduleAutosave (500ms).
  _scheduleAutosave(record, data) {
    clearTimeout(this._autosaveTimer)
    this._pendingAutosave = { record, data }
    this._autosaveTimer = setTimeout(() => this._flushAutosave(), 500)
  }

  _cancelAutosave() {
    clearTimeout(this._autosaveTimer)
    this._pendingAutosave = null
  }

  // Runs a pending autosave now -- leaving the survey inside the debounce
  // window would otherwise drop the last change.
  _flushAutosave() {
    const pending = this._pendingAutosave
    this._cancelAutosave()
    if (pending) this._savePartialToCache(pending.record, pending.data)
  }

  // Upserts (card #3827, same as result_editor_controller since #3804) --
  // previously update-only, so a response whose cached row had gone missing
  // saved nothing. The new-row shape mirrors the editor's
  // _newCachedResponseFields.
  async _savePartialToCache(record, data) {
    try {
      const responseId = Number(record.responseId)
      const existing = await db.cached_responses
        .where("responseId").equals(responseId)
        .first()
      if (existing) {
        await db.cached_responses.update(existing.id, { resultJson: data })
      } else {
        await db.cached_responses.add({
          responseId,
          placeId:      record.placeId ?? null,
          scheduledAt:  record.scheduledAt ?? null,
          cachedAt:     new Date().toISOString(),
          status:       record.status || "started",
          missionName:  record.missionName || null,
          missionPriority: record.missionPriority ?? null,
          completeUrl:  record.completeUrl || null,
          surveyJson:   record.surveyJson,
          placeContext: record.placeContext || {},
          resultJson:   data
        })
        logEvent("response", "Autosave created missing cached_responses row", {
          responseId: record.responseId
        }).catch(() => {})
      }
    } catch (error) {
      logError("response", "Autosave failed", {
        responseId: record.responseId,
        message:    error?.message || String(error)
      }).catch(() => {})
    }
  }

  async _handleUpload(survey, options) {
    const results = []
    const errors  = []
    for (const file of options.files) {
      let compressed
      try {
        compressed = await compressImageWithRetry(file)
      } catch (error) {
        // Compression exhausted its retry ladder (or the file exceeded the
        // hard size cap) -- skip this file rather than queueing an
        // uncompressed multi-MB original (card #2433). Other files in the
        // batch still get queued.
        // error.message is already reason-specific (card #3363: HEIC that
        // couldn't be converted, or a video), so the flash below says why.
        logError("upload", "Photo compression exhausted", { message: error?.message, cause: error?.cause?.message || error?.cause?.name || null, size: file.size, reason: error?.reason || null, detectedType: error?.detectedType || file.type || null, heicConversionAttempted: !!error?.heicConversionAttempted }).catch(() => {})
        errors.push(error?.message || "Photo too large — try again")
        continue
      }
      // Structured name (card #3827) -- the raw SurveyJS camera file is
      // "snap_picture", which says nothing about where the photo belongs.
      // This shell is served without a session and carries no user id, so
      // the user segment is "unknown"; the server stamps Image#user_id anyway.
      // options.name is the SurveyJS question name (UploadFilesEvent), the
      // same value result_editor_controller passes as questionName.
      const named = withImageFilename(compressed, {
        responseId:   this._activeRecord?.responseId,
        questionName: options?.name
      })
      // Card #3994 -- try the network first, from the bytes still in memory.
      // This shell is what a Poor Signal visit runs in, and it used to write
      // every photo straight to IndexedDB with no attempt at all; an iPhone
      // often cannot read such a photo back. Poor Signal only buys the longer
      // timeout. The CSRF token comes from the session store (the shell page
      // is cached, so its own meta tag may be stale). The photo is queued only
      // when the attempt fails, under the same capture id.
      const responseId = this._activeRecord?.responseId
      const captureId  = newCaptureId()
      const attempt = await uploadPhotoFromMemory(named, {
        captureId,
        surveyableId:   String(responseId),
        surveyableType: "Response",
        csrfToken:      await readCsrfToken().catch(() => null),
        poorSignal:     await placeIsPoorSignal(this._activeRecord?.placeId)
      })
      if (attempt.ok) {
        results.push({ file: named, content: attempt.data.url })
        continue
      }

      const { localUrl } = await queueImage(named, {
        surveyableId:   String(responseId),
        surveyableType: "Response",
        responseId:     responseId != null ? String(responseId) : null,
        placeId:        this._activeRecord?.placeId    != null ? String(this._activeRecord.placeId)    : null,
        questionName:   options?.name || null,
        blobKey:        captureId
      })
      results.push({ file: named, content: localUrl })
    }
    if (errors.length) showFieldFlash(errors[0])
    options.callback(results, errors.length ? errors : undefined)
  }

  // Card #4451 -- relaxRequired must only be true for an already-ended
  // response being re-edited (MC flow); a first-time offline completion
  // needs isRequired left alone or a Tasker can submit with required
  // questions blank.
  _convertPhotoQuestions(surveyJson, relaxRequired) {
    if (!surveyJson?.pages) return
    const convert = (elements) => {
      if (!Array.isArray(elements)) return
      elements.forEach(el => {
        if (["file", "camera-upload-question", "camera-mult-upload-question"].includes(el.type)) {
          el.type = "file"
          el.allowMultiple = true
          el.sourceType = "file-camera"
          el.waitForUpload = true
          el.storeDataAsText = false
          // Match the compressor's input cap (#3494, #3733) -- SurveyJS rejects
          // anything over maxSize before our compression pipeline ever runs.
          el.maxSize = MAX_INPUT_BYTES
        }
        if (relaxRequired) el.isRequired = false
        if (el.type === "panel" && Array.isArray(el.elements)) convert(el.elements)
      })
    }
    surveyJson.pages.forEach(p => convert(p.elements))
  }

  _showList() {
    if (this.hasListViewTarget) this.listViewTarget.classList.remove("hidden")
    if (this.hasSurveyViewTarget) this.surveyViewTarget.classList.add("hidden")
    if (this.hasPlacesViewTarget) this.placesViewTarget.classList.add("hidden")
    if (this.hasPlaceShowViewTarget) this.placeShowViewTarget.classList.add("hidden")
  }

  async _showSurvey(record) {
    if (this.hasListViewTarget) this.listViewTarget.classList.add("hidden")
    if (this.hasPlacesViewTarget) this.placesViewTarget.classList.add("hidden")
    if (this.hasPlaceShowViewTarget) this.placeShowViewTarget.classList.add("hidden")
    if (this.hasSurveyViewTarget) this.surveyViewTarget.classList.remove("hidden")
    if (this.hasSurveyTitleTarget) this.surveyTitleTarget.textContent = record.missionName || "Survey"
    if (this.hasSurveySubtitleTarget) {
      const place = record.placeId ? await db.cached_places.get(record.placeId) : null
      this.surveySubtitleTarget.textContent = place?.name || ""
    }
  }

  _showPlaceShow() {
    if (this.hasListViewTarget) this.listViewTarget.classList.add("hidden")
    if (this.hasSurveyViewTarget) this.surveyViewTarget.classList.add("hidden")
    if (this.hasPlacesViewTarget) this.placesViewTarget.classList.add("hidden")
    if (this.hasPlaceShowViewTarget) this.placeShowViewTarget.classList.remove("hidden")
    this._inPlaceShow = true
  }

  // -- places view --

  _isPlacesPath() {
    return window.location.pathname.startsWith("/field/places")
  }

  _isPlaceShowPath() {
    return /^\/field\/places\/\d+/.test(window.location.pathname)
  }

  _placeIdFromPath() {
    const match = window.location.pathname.match(/^\/field\/places\/(\d+)/)
    return match ? parseInt(match[1], 10) : null
  }

  _showPlaces() {
    if (this.hasListViewTarget) this.listViewTarget.classList.add("hidden")
    if (this.hasSurveyViewTarget) this.surveyViewTarget.classList.add("hidden")
    if (this.hasPlacesViewTarget) this.placesViewTarget.classList.remove("hidden")
  }

  async _renderPlaces() {
    const places = await db.cached_places.toArray()

    if (this.hasPlacesListTarget) this.placesListTarget.innerHTML = ""

    if (places.length === 0) {
      if (this.hasPlacesEmptyTarget) this.placesEmptyTarget.classList.remove("hidden")
      if (this.hasPlacesListTarget) this.placesListTarget.classList.add("hidden")
      return
    }

    if (this.hasPlacesEmptyTarget) this.placesEmptyTarget.classList.add("hidden")
    if (this.hasPlacesListTarget) this.placesListTarget.classList.remove("hidden")

    // Offline notice header
    const notice = document.createElement("div")
    notice.className = "mb-3 px-3 py-2.5 bg-slate-100 rounded-xl"
    notice.innerHTML = `<p class="text-xs text-slate-500">You're offline. Showing ${places.length} place${places.length === 1 ? "" : "s"} from your last sync. Places with the <strong>Offline ready</strong> badge are fully prepared for offline use.</p>`
    this.placesListTarget.appendChild(notice)

    // Count responses per place from cached_responses
    const allResponses = await db.cached_responses.toArray()
    const countsByPlace = new Map()
    for (const r of allResponses) {
      if (!r.placeId) continue
      if (!countsByPlace.has(r.placeId)) {
        countsByPlace.set(r.placeId, { todo: 0, started: 0, stopped: 0 })
      }
      const counts = countsByPlace.get(r.placeId)
      if (counts[r.status] !== undefined) {
        counts[r.status]++
      } else {
        counts.todo++
      }
    }

    for (const place of places) {
      const counts = countsByPlace.get(place.placeId) || { todo: 0, started: 0, stopped: 0 }
      this.placesListTarget.appendChild(this._buildPlaceCard(place, counts))
    }
  }

  // -- place show shell --
  //
  // When the SW serves the /field/places shell for a /field/places/:id URL,
  // this renders the individual place page from cached_places + cached_responses.

  // place-sort:applied from the sort sheet (card #4098): re-sort the cached
  // missions for the place on screen. IndexedDB only, no network.
  resortPlaceShow() {
    if (this._placeShowId == null) return
    this._renderPlaceShow(this._placeShowId)
  }

  async _renderPlaceShow(placeId) {
    this._placeShowId = placeId
    // A sort tap or a sync can start a render while one is still reading
    // IndexedDB; only the latest may write, or the cards appear twice.
    const renderToken = this._placeShowRenderToken = (this._placeShowRenderToken || 0) + 1
    const place = await db.cached_places.get(placeId)
    const responses = place ? await db.cached_responses.where("placeId").equals(placeId).toArray() : []
    if (renderToken !== this._placeShowRenderToken) return

    if (this.hasPlaceShowContainerTarget) this.placeShowContainerTarget.innerHTML = ""

    if (!place) {
      if (this.hasPlaceShowContainerTarget) {
        const missing = document.createElement("p")
        missing.className = "text-sm text-slate-400 text-center py-6"
        missing.textContent = "Place not found in offline cache."
        this.placeShowContainerTarget.appendChild(missing)
      }
      return
    }

    if (this.hasPlaceShowTitleTarget) this.placeShowTitleTarget.textContent = place.name || "Place"

    // Same order as the online place page (card #4098): status group, then
    // the Tasker's saved sort (default priority), then name.
    const sorted = sortCachedResponses(responses, readMissionSort())

    // Place header card
    const header = document.createElement("div")
    header.className = "bg-white rounded-xl border border-slate-200 p-4 mb-1 mt-2"
    const city  = place.address?.city
    const state = place.address?.state
    const locationText = [city, state].filter(Boolean).join(", ")
    const badgeParts = []
    if (place.bad_connectivity) {
      badgeParts.push(`<span class="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700"><svg xmlns="http://www.w3.org/2000/svg" class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z"/></svg>Offline ready</span>`)
    }
    header.innerHTML = `
      <p class="font-semibold text-slate-800 leading-snug">${this._escHtml(place.name || "Unknown place")}</p>
      ${locationText ? `<p class="text-xs text-slate-400 mt-0.5">${this._escHtml(locationText)}</p>` : ""}
      ${badgeParts.length ? `<div class="mt-2 flex flex-wrap gap-1">${badgeParts.join("")}</div>` : ""}
    `
    this.placeShowContainerTarget.appendChild(header)

    // Response cards for this place
    if (sorted.length === 0) {
      const empty = document.createElement("p")
      empty.className = "text-sm text-slate-400 text-center py-6"
      empty.textContent = "No missions cached for this place."
      this.placeShowContainerTarget.appendChild(empty)
      return
    }

    for (const record of sorted) {
      const statusColour  = this._statusColour(record.status)
      const scheduledText = record.scheduledAt
        ? new Date(record.scheduledAt).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" })
        : "Unscheduled"

      const card = document.createElement("div")
      card.className = "bg-white rounded-2xl border border-slate-200 p-4 mb-2"

      // Card #3827 -- started shows as In Progress with Continue (it used to
      // show "started" and, after an online Start the cache never heard about,
      // "Start Mission"); scheduled keeps Start; ended can't be reopened.
      const header = `
          <div class="flex items-start justify-between gap-3">
            <p class="text-sm font-semibold text-slate-800 truncate">${this._escHtml(record.missionName || "Survey")}</p>
            <span class="shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColour}">
              ${this._escHtml(this._statusLabel(record.status))}
            </span>
          </div>`
      const startPrompt = this._startPrompt(record)
      const actionLabel = startableNow(record)         ? "Start"
                        : record.status === "started"  ? "Continue"
                        :                                 null

      if (actionLabel) {
        card.innerHTML = `
          ${header}
          <p class="text-xs text-slate-400 mt-2 mb-3">${scheduledText}</p>
          <button type="button" class="bg-sapphire text-white text-sm font-semibold px-4 py-2 rounded-xl active:opacity-80">
            ${actionLabel}
          </button>
        `
        const open = record.status === "scheduled"
          ? () => this._startAndOpenSurvey(record)
          : () => this._openSurvey(record)
        card.querySelector("button").addEventListener("click", open)
      } else if (record.status === "ended") {
        card.innerHTML = `
          ${header}
          <p class="text-xs text-slate-400 mt-2">${scheduledText}</p>
        `
      } else if (startPrompt) {
        // Card #4152: not startable yet. Scheduling happens online, on the card.
        card.innerHTML = `
          ${header}
          <p class="text-xs text-slate-400 mt-2">${scheduledText}</p>
          <p class="text-xs font-medium text-slate-500 mt-1">${this._escHtml(startPrompt)}</p>
        `
      } else {
        card.classList.add("active:bg-slate-50", "cursor-pointer")
        card.innerHTML = `
          ${header}
          <p class="text-xs text-slate-400 mt-2">${scheduledText}</p>
        `
        card.addEventListener("click", () => this._openSurvey(record))
      }

      this.placeShowContainerTarget.appendChild(card)
    }
  }

  async _startAndOpenSurvey(record) {
    const existing = await db.cached_responses
      .where("responseId").equals(record.responseId)
      .first()
    if (existing) {
      await db.cached_responses.update(existing.id, { status: "started" })
    }
    record.status = "started"
    logEvent("response", "Response started offline", {
      responseId: record.responseId,
      cached:     !!existing
    }).catch(() => {})
    this._openSurvey(record)
  }

  _buildPlaceCard(place, counts) {
    const wrapper = document.createElement("div")
    wrapper.className = "bg-white rounded-xl border border-slate-200 p-4 shadow-sm"

    const total      = Object.values(counts).reduce((a, b) => a + b, 0)
    const badgeParts = [
      counts.todo    > 0 ? `<span class="text-xs text-slate-500">${counts.todo} to do</span>` : "",
      counts.started > 0 ? `<span class="text-xs text-amber-600">${counts.started} in progress</span>` : "",
      counts.stopped > 0 ? `<span class="text-xs text-orange-600">${counts.stopped} stopped</span>` : ""
    ].filter(Boolean)

    const countHtml = badgeParts.length > 0
      ? badgeParts.join('<span class="text-slate-300 mx-1">·</span>')
      : '<span class="text-xs text-slate-400">No missions</span>'

    const connectivityBadge = place.bad_connectivity
      ? `<span class="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700">
           <svg xmlns="http://www.w3.org/2000/svg" class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
             <path stroke-linecap="round" stroke-linejoin="round" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
           </svg>
           Offline ready
         </span>`
      : ""

    wrapper.innerHTML = `
      <div class="flex items-start justify-between gap-3">
        <div class="flex-1 min-w-0">
          <p class="font-semibold text-slate-800 leading-snug truncate">${this._escHtml(place.name || "Unknown place")}</p>
          <div class="flex items-center gap-1 mt-1 flex-wrap">${countHtml}</div>
        </div>
        ${connectivityBadge}
      </div>
      <p class="text-xs text-slate-400 mt-1.5">${total} mission${total === 1 ? "" : "s"} cached</p>
    `
    return wrapper
  }

  // -- surveys empty state --

  _showEmpty() {
    if (this.hasEmptyTarget) this.emptyTarget.classList.remove("hidden")
    if (this.hasListTarget) this.listTarget.classList.add("hidden")
    // iOS eviction guard: if data was previously synced but cache is now empty,
    // show a more specific prompt rather than "you've never synced"
    if (this.hasEmptyTextTarget) {
      const lastSync = localStorage.getItem("field:last-prefetch")
      this.emptyTextTarget.textContent = lastSync
        ? "Your offline data needs refreshing. Connect and tap Sync."
        : "Connect and tap Sync to download your surveys for offline use."
    }
  }

  _hideEmpty() {
    if (this.hasEmptyTarget) this.emptyTarget.classList.add("hidden")
    if (this.hasListTarget) this.listTarget.classList.remove("hidden")
  }

  _statusColour(status) {
    const map = {
      todo:       "bg-slate-100 text-slate-600",
      scheduled:  "bg-blue-50 text-blue-700",
      started:    "bg-amber-50 text-amber-700",
      stopped:    "bg-orange-50 text-orange-700",
      ended:      "bg-emerald-50 text-emerald-700"
    }
    return map[status] || "bg-slate-100 text-slate-600"
  }

  // The words the rest of the field app uses for each status (server cards:
  // "In Progress" for started). Unknown statuses show as they are.
  _statusLabel(status) {
    const map = {
      todo:      "To do",
      scheduled: "Scheduled",
      started:   "In Progress",
      stopped:   "Stopped",
      ended:     "Completed"
    }
    return map[status] || status
  }

  _escHtml(str) {
    return String(str || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  }
}
